import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import ArrowLeftIcon from '@material-ui/icons/ArrowLeft';
import { Box, Button, makeStyles, Paper, Typography } from '@material-ui/core';
import classNames from 'classnames';

import CloseButton from 'components/CustomButtons/CloseButton';
import { closeModalPage, openModalPage } from 'actions/modalPage';
import { modalPages as mp } from 'constants/modalPage';
import Icon from 'components/Icon';
import {
  getCustomerIssuedCoupons,
  getIsDefaultCustomer,
  getSearchCustomerGroups,
  getSelectedCustomer,
} from 'reducers/customerSearch';
import CustomerSearch from 'containers/Col1/Search/Customers';
import {
  getClientCode,
  getLoggedInEmployeeID,
  getSessionKey,
  getUserLoggedIn,
} from 'reducers/Login';
import { getSelectedPos } from 'reducers/PointsOfSale';
import { RootState } from 'reducers';

import singlePrompt, { couponsPrompt } from '../utils';
import {
  getTafReduxState,
  getIsSchoolEntitySelected,
  getIsMedicalEntitySelected,
} from '../rdx/reducers';
import {
  resetEditState,
  setSelectedEntity,
  toggleApplyTafLogic,
  toggleDisplayPreviousEntities,
} from '../rdx/actions';

import './components.scss';

const CustomerData = () => {
  const dispatch = useDispatch();
  const { address, mobile, email } = useSelector(getSelectedCustomer);
  const issuedCoupons = useSelector(getCustomerIssuedCoupons);
  const customer = useSelector(getSelectedCustomer);
  const { globalMedicalEntity, globalSchoolEntity } = useSelector(
    getTafReduxState,
  );

  return (
    <div className="taf-customer-data-container">
      {issuedCoupons?.length > 0 ? (
        <p
          className="vouchers"
          onClick={() => {
            dispatch(singlePrompt('SearchCouponsModal'));
          }}
        >
          {issuedCoupons.length} vouchers available
        </p>
      ) : null}
      <p>{address}</p>
      {mobile ? (
        <p>
          <strong>Phone:</strong> {mobile}
        </p>
      ) : null}
      <p>
        <strong>E-mail:</strong> {email}
      </p>
      <p>
        <strong>School / club: </strong>
        {globalSchoolEntity.entityName}
      </p>
      <p>
        <strong>TAF Medical: </strong>
        {globalMedicalEntity.entityName}
      </p>
      <span
        className="taf-user-badge-icon-span icon_plus_alt"
        onClick={() => {
          return dispatch(couponsPrompt());
        }}
      />
      <Icon
        style={{ paddingLeft: '12px', paddingRight: '12px' }}
        key="taf-icon-edit"
        name="icon_pencil-edit"
        title="taf-icon-edit"
        action={() =>
          dispatch(
            openModalPage({
              component: mp.createCustomer,
              groupID: mp.createCustomer,
              props: { customer },
            }),
          )
        }
      />
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  tafCustomerDetailsContainer: {
    height: '80%',
    maxWidth: 'none',
    backgroundColor: 'transparent',
    position: 'relative',
    overflow: 'visible',
    border: '1px solid rgba(255,255,255 , 0.23)',
    boxShadow: 'none',
  },
  customerDataContainer: {
    display: 'flex',
    flexFlow: 'column',
    height: '100%',
    padding: '10px',
    '& > div': {
      minHeight: '50%',
    },
  },
  arrowIcon: {
    position: 'absolute',
    fontSize: '3em',
    zIndex: 99,
    [theme.breakpoints.up('sm')]: {
      top: '40%',
      left: '-25px',
    },
    [theme.breakpoints.down('sm')]: {
      transform: 'rotate(90deg)',
      left: '50%',
      top: '-25px',
    },
  },
  buttonsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '10px',
  },
  actionButton: {
    maxWidth: '50%',
    flexBasis: '49%',
    height: '39px',
    '&:not(:last-child)': {
      marginRight: '10px',
    },
  },
  customerResults: {
    maxHeight: 'calc(100vh - 270px)',
    marginBottom: '54px',
  },
}));

const TafCustomerDetails = () => {
  const styles = useStyles();
  const isDefaultCustomer = useSelector(getIsDefaultCustomer);
  const isSchoolSelected = useSelector(getIsSchoolEntitySelected);
  const isMedicalSelected = useSelector(getIsMedicalEntitySelected);
  const currentCustomer = useSelector(getSelectedCustomer);
  const customerGroups = useSelector(getSearchCustomerGroups);
  const { customerGroupID } =
    customerGroups.find(group => group.name.toUpperCase() === 'CLUBFIT') ?? {};

  const dispatch = useDispatch();

  const shouldHideActionButtons =
    isDefaultCustomer || currentCustomer.groupID !== customerGroupID;

  return (
    <>
      <Paper className={styles.tafCustomerDetailsContainer}>
        <div className={styles.customerDataContainer}>
          {shouldHideActionButtons ? null : (
            <div>
              <CustomerData />
            </div>
          )}
        </div>
        <ArrowLeftIcon className={styles.arrowIcon} />
      </Paper>
      <div className={styles.buttonsContainer}>
        {shouldHideActionButtons ? null : (
          <>
            <Button
              className={classNames(
                styles.actionButton,
                isSchoolSelected ? 'selected-entity' : 'default-entity',
              )}
              variant="contained"
              onClick={async () => {
                await dispatch(toggleApplyTafLogic(true));
                await dispatch(setSelectedEntity('association'));

                return dispatch(singlePrompt('TafCustomerSearchModal'));
              }}
            >
              LINK SCHOOL
            </Button>
            <Button
              className={classNames(
                styles.actionButton,
                isMedicalSelected ? 'selected-entity' : 'default-entity',
              )}
              variant="contained"
              onClick={async () => {
                await dispatch(toggleApplyTafLogic(true));
                await dispatch(setSelectedEntity('proffesional'));

                return dispatch(singlePrompt('TafCustomerSearchModal'));
              }}
            >
              LINK MEDICAL
            </Button>
          </>
        )}
      </div>
    </>
  );
};

export const TafCustomerSearchModal = () => {
  const styles = useStyles();
  const dispatch: ThunkDispatch<RootState, unknown, Action> = useDispatch();
  const { isEditMode } = useSelector(getTafReduxState);

  useEffect(() => {
    dispatch(toggleDisplayPreviousEntities(true));
    return () => {
      dispatch(toggleDisplayPreviousEntities(false));
    };
  }, [toggleDisplayPreviousEntities, dispatch]);

  const onCancel = () => {
    if (isEditMode) {
      dispatch(resetEditState());
    }

    dispatch(toggleDisplayPreviousEntities(false));
    dispatch(closeModalPage());
  };

  const close = useCallback(() => {
    dispatch(closeModalPage());
  }, []);

  return (
    <Box padding="1rem">
      <Box display="flex" alignItems="center" marginBottom="0.75rem">
        <Box>
          <Typography variant="h5">Select customer</Typography>
        </Box>
        <Box display="flex" justifyContent="flex-end" flex={1}>
          <CloseButton action={() => onCancel()} />
        </Box>
      </Box>
      <Box className={styles.customerResults}>
        <CustomerSearch active={true} closePopover={close} isPopover={false} />
      </Box>
    </Box>
  );
};

export const TafCouponsPoints = ({ closeModal }) => {
  const { loginUrl, userID } = useSelector(getUserLoggedIn);
  const { customerID } = useSelector(getSelectedCustomer);
  const employeeID = useSelector(getLoggedInEmployeeID);
  const { pointOfSaleID, warehouseID } = useSelector(getSelectedPos);
  const sessionKey = useSelector(getSessionKey);
  const code = useSelector(getClientCode);

  return (
    <Box padding="1rem">
      <Box display="flex" alignItems="center" marginBottom="0.75rem">
        <Box>
          <Typography variant="h5">Points</Typography>
        </Box>
        <Box display="flex" justifyContent="flex-end" flex={1}>
          <CloseButton action={closeModal} />
        </Box>
      </Box>
      <Box>
        <iframe
          title="test"
          src={`${loginUrl}hosted_plugin.php?plugin=AdjustRewardPointsInPOS&code=${code}&sessionKey=${sessionKey}&customerID=${customerID}&userID=${userID}&employeeID=${employeeID}&pointOfSaleID=${pointOfSaleID}&warehouseID=${warehouseID}`}
          style={{ height: '350px', borderStyle: 'none' }}
        />
      </Box>
    </Box>
  );
};

export default TafCustomerDetails;
